/* ====================================================================
 *  remote — усе I/O кошика в Firestore (`users/{uid}/cartItems`).
 *  Без Vue і без Pinia: чисті функції над (db, uid), щоб стор лишався
 *  оркестрацією, а не шаром доступу до даних.
 *
 *  ЧОМУ БЕЗ orderBy: раніше читання йшли з orderBy("updatedAt","desc").
 *  Firestore виключає з такого запиту документи БЕЗ цього поля, тож
 *  стара позиція без updatedAt зникала з merge при логіні, лишалась у
 *  базі й не потрапляла в лічильник кошика. Сортування тепер клієнтське
 *  і детерміноване (sortByKey) — той самий порядок, що й у снапшота.
 * ==================================================================== */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  writeBatch,
  type Firestore,
  type Unsubscribe,
} from "firebase/firestore";

import {
  toCartItem,
  toRemoteFields,
  sortByKey,
  type CartItem,
} from "./mapping";

export function cartCol(db: Firestore, uid: string) {
  return collection(db, "users", uid, "cartItems");
}

/** Усі позиції віддаленого кошика, відсортовані за ключем. */
export async function fetchCartItems(
  db: Firestore,
  uid: string,
): Promise<CartItem[]> {
  const snap = await getDocs(cartCol(db, uid));
  return sortByKey(snap.docs.map((d) => toCartItem(d.id, d.data())));
}

/** Живий кошик: підписка на колекцію. Повертає функцію відписки. */
export function subscribeCartItems(
  db: Firestore,
  uid: string,
  onChange: (_list: CartItem[]) => void,
  onError?: (_e: unknown) => void,
): Unsubscribe {
  return onSnapshot(
    cartCol(db, uid),
    (snap) => {
      onChange(sortByKey(snap.docs.map((d) => toCartItem(d.id, d.data()))));
    },
    // без цього колбека permission-denied на лістені гине мовчки
    (e) => onError?.(e),
  );
}

/** Апсерт позицій (merge) зі службовою міткою часу одного типу. */
export async function upsertItems(
  db: Firestore,
  uid: string,
  items: CartItem[],
): Promise<void> {
  const base = cartCol(db, uid);
  await Promise.all(
    items.map((i) =>
      setDoc(
        doc(base, i.key),
        { ...toRemoteFields(i), updatedAt: serverTimestamp() },
        { merge: true },
      ),
    ),
  );
}

export async function deleteKey(
  db: Firestore,
  uid: string,
  key: string,
): Promise<void> {
  await deleteDoc(doc(cartCol(db, uid), key));
}

export async function deleteKeys(
  db: Firestore,
  uid: string,
  keys: string[],
): Promise<void> {
  const list = keys.filter(Boolean);
  if (!list.length) return;
  const base = cartCol(db, uid);
  const batch = writeBatch(db);
  for (const k of list) batch.delete(doc(base, k));
  await batch.commit();
}

/** Повне очищення віддаленого кошика. */
export async function clearRemoteCart(
  db: Firestore,
  uid: string,
): Promise<void> {
  const snap = await getDocs(cartCol(db, uid));
  if (snap.empty) return;
  const batch = writeBatch(db);
  snap.docs.forEach((d) => batch.delete(d.ref));
  await batch.commit();
}

/** Точкове оновлення картинок (гідрація з варіантів). */
export async function patchImages(
  db: Firestore,
  uid: string,
  updates: { key: string; image: string }[],
): Promise<void> {
  if (!updates.length) return;
  const base = cartCol(db, uid);
  const batch = writeBatch(db);
  for (const u of updates) {
    batch.set(
      doc(base, u.key),
      { image: u.image, updatedAt: serverTimestamp() },
      { merge: true },
    );
  }
  await batch.commit();
}

/* ---- читання варіантів товару (потрібне гідрації і міграції) ---- */

export async function fetchVariantDoc(
  db: Firestore,
  productId: string,
  variantId: string,
): Promise<Record<string, unknown> | null> {
  try {
    const snap = await getDoc(
      doc(db, "products", String(productId), "variants", String(variantId)),
    );
    return snap.exists() ? (snap.data() as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

export async function fetchProductVariants(
  db: Firestore,
  productId: string,
): Promise<Record<string, unknown>[]> {
  if (!productId) return [];
  try {
    const snap = await getDocs(
      collection(db, "products", String(productId), "variants"),
    );
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch {
    return [];
  }
}
